import { isValidGtin } from './barcode/ean';
import { LAYOUT_OVERFLOW_MARGIN_MM } from './layout-v2/layout-canvas.constants';

/** The editor protocol is deliberately separate from the legacy PDF tokens. */
export const PREPACKAGED_LABEL_LAYOUT_DOCUMENT_VERSION = 2 as const;

export const PREPACKAGED_LABEL_EDITOR_PRESET_IDS = [
  'CLASSIC_STACKED',
  'COMPACT_TWO_COLUMN',
  'BRAND_HEADER',
  'DENSE_TECHNICAL',
] as const;
export type PrepackagedLabelEditorPresetId = (typeof PREPACKAGED_LABEL_EDITOR_PRESET_IDS)[number];

export const PREPACKAGED_LABEL_EDITOR_BLOCK_IDS = [
  'PRODUCT_NAME',
  'INGREDIENTS',
  'ALLERGENS',
  'NUTRITION',
  'NET_QUANTITY',
  'DATE_MARK',
  'LOT',
  'OSA',
  'STORAGE',
  'USAGE',
  'ORIGIN',
  'ALCOHOL',
  'DRAINED_WEIGHT',
  'QUID',
  'PACKAGING_DISPOSAL',
  'CUSTOM_SECTIONS',
  'RAW_WARNING',
  // B / G3-G5. In coda, mai dentro un blockOrder di preset: i preset sono
  // dati di protocollo congelati nel payload V5 e `isCanonicalPrepackagedLabelEditorPreset`
  // confronta il manifesto con questo catalogo — cambiare un `blockOrder`
  // invaliderebbe ogni payload V5 già scritto (R4).
  'ESTABLISHMENT',
  'IDENTIFICATION_MARK',
  // C / G7. Logo biologico UE, codice dell'organismo di controllo e origine
  // delle materie prime agricole, in quest'ordine fisso e non riordinabile —
  // vedi la nota sopra: sempre in coda, mai dentro un `blockOrder` di preset.
  'ORGANIC',
  // F / G11. Dir. 76/211/CEE All. I punto 3.3: il solo glifo «℮», stesso
  // campo visivo della quantità nominale. Stessa nota di ordinamento fisso.
  'E_MARK',
  // Indicazioni volontarie SENZA licenza (claim testuali senza glutine/vegano
  // + pittogramma UE bicchiere-forchetta, pubblico dominio). In coda, mai
  // dentro un blockOrder di preset: stessa nota di ordinamento fisso.
  'VOLUNTARY_MARKS',
  // D / G6. Diciture d'origine dei regimi verticali, stampate verbatim come le
  // scrive il decreto. Stessa nota di ordinamento fisso: sempre in coda, mai
  // dentro un `blockOrder` di preset.
  'VERTICAL_ORIGIN',
  'PRIMARY_INGREDIENT_ORIGIN',
] as const;
export type PrepackagedLabelEditorBlockId = (typeof PREPACKAGED_LABEL_EDITOR_BLOCK_IDS)[number];

/**
 * All. V punto 1.7 Reg. (UE) 2018/848: altezza minima 9 mm, larghezza minima
 * 13,5 mm, proporzione altezza:larghezza sempre 1:1,5. La deroga a 6 mm "in
 * via del tutto eccezionale per confezioni molto piccole" non è applicata
 * automaticamente: l'allegato non la definisce con una soglia calcolabile
 * (decisione del proprietario del prodotto, 19 agosto 2026).
 */
export const ORGANIC_LOGO_MIN_HEIGHT_MM = 9;
export const ORGANIC_LOGO_MIN_WIDTH_MM = 13.5;
export const ORGANIC_LOGO_ASPECT_RATIO = 1.5;
/** All. V punto 1.7: deroga NON applicata automaticamente — vedi sopra. */
export const ORGANIC_LOGO_EXCEPTIONAL_MIN_HEIGHT_MM = 6;
/** All. V punto 1.2, come modificato dal Reg. delegato (UE) 2024/2867. */
export const ORGANIC_LOGO_REFERENCE_RGB = { r: 169, g: 201, b: 56 } as const;
export const ORGANIC_LOGO_REFERENCE_CMYK = { c: 50, m: 0, y: 100, k: 0 } as const;

export const PREPACKAGED_LABEL_EDITOR_ACTIONS = [
  'APPLY_PRESET',
  'SET_LABEL_FIELD',
  'SET_LEGAL_DECISION',
  'ADD_REQUIRED_BLOCK',
  'SET_BLOCK_ORDER',
  'USE_DERIVED_INGREDIENT_LIST',
  'USE_DERIVED_ALLERGEN_EMPHASIS',
  'ACCEPT_EXTRACTED_ASSET',
  'NAVIGATE_TO_FIELD',
  'OPEN_RECIPE_SOURCE',
] as const;
export type PrepackagedLabelEditorActionType = (typeof PREPACKAGED_LABEL_EDITOR_ACTIONS)[number];

export type PrepackagedLabelEditorPreset = {
  readonly id: PrepackagedLabelEditorPresetId;
  readonly manifestVersion: 1;
  readonly blockOrder: readonly PrepackagedLabelEditorBlockId[];
  readonly columns: 1 | 2;
  readonly typography: {
    readonly fontFamily: string;
    readonly baseFontSizeMm: number;
    readonly minXHeightMm: number;
  };
};

const preset = (
  id: PrepackagedLabelEditorPresetId,
  blockOrder: readonly PrepackagedLabelEditorBlockId[],
  columns: 1 | 2,
  typography: PrepackagedLabelEditorPreset['typography'],
): PrepackagedLabelEditorPreset =>
  Object.freeze({
    id,
    manifestVersion: 1 as const,
    blockOrder: Object.freeze([...blockOrder]),
    columns,
    typography: Object.freeze({ ...typography }),
  });

/**
 * Presets are protocol data, not UI defaults. Their manifests are persisted
 * in V5 and must never be mutated in place by an editor or an AI action.
 */
export const PREPACKAGED_LABEL_EDITOR_PRESETS: readonly PrepackagedLabelEditorPreset[] =
  Object.freeze([
    preset(
      'CLASSIC_STACKED',
      ['PRODUCT_NAME', 'INGREDIENTS', 'NUTRITION', 'NET_QUANTITY', 'DATE_MARK', 'LOT', 'OSA'],
      1,
      { fontFamily: 'Noto Sans', baseFontSizeMm: 3.2, minXHeightMm: 1.2 },
    ),
    preset(
      'COMPACT_TWO_COLUMN',
      ['PRODUCT_NAME', 'NET_QUANTITY', 'DATE_MARK', 'LOT', 'INGREDIENTS', 'NUTRITION', 'OSA'],
      2,
      { fontFamily: 'Noto Sans', baseFontSizeMm: 2.8, minXHeightMm: 1.2 },
    ),
    preset(
      'BRAND_HEADER',
      ['PRODUCT_NAME', 'OSA', 'INGREDIENTS', 'NUTRITION', 'NET_QUANTITY', 'DATE_MARK', 'LOT'],
      1,
      { fontFamily: 'Noto Sans', baseFontSizeMm: 3.2, minXHeightMm: 1.2 },
    ),
    preset(
      'DENSE_TECHNICAL',
      ['PRODUCT_NAME', 'INGREDIENTS', 'NUTRITION', 'NET_QUANTITY', 'DATE_MARK', 'LOT', 'OSA'],
      2,
      { fontFamily: 'Noto Sans', baseFontSizeMm: 2.4, minXHeightMm: 1.2 },
    ),
  ]);

type ParseSuccess<T> = { success: true; data: T };
type ParseFailure = { success: false; error: Error };
export interface ContractSchema<T> {
  safeParse(input: unknown): ParseSuccess<T> | ParseFailure;
  parse(input: unknown): T;
}

function contractSchema<T>(guard: (input: unknown) => input is T): ContractSchema<T> {
  return {
    safeParse(input) {
      return guard(input)
        ? { success: true, data: input }
        : { success: false, error: new Error('invalid prepackaged label contract') };
    },
    parse(input) {
      if (!guard(input)) {
        throw new Error('invalid prepackaged label contract');
      }
      return input;
    },
  };
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;
const hasExactKeys = (value: Record<string, unknown>, keys: readonly string[]): boolean => {
  const actual = Object.keys(value).sort();
  return (
    actual.length === keys.length && actual.every((key, index) => key === [...keys].sort()[index])
  );
};
const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;
const isPositiveMm = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;
const isPresetId = (value: unknown): value is PrepackagedLabelEditorPresetId =>
  typeof value === 'string' &&
  (PREPACKAGED_LABEL_EDITOR_PRESET_IDS as readonly string[]).includes(value);
const isBlockId = (value: unknown): value is PrepackagedLabelEditorBlockId =>
  typeof value === 'string' &&
  (PREPACKAGED_LABEL_EDITOR_BLOCK_IDS as readonly string[]).includes(value);
const isContentHash = (value: unknown): value is string =>
  typeof value === 'string' && /^[0-9a-f]{64}$/.test(value);

export const PREPACKAGED_LABEL_EDITOR_PRESET_ID_SCHEMA: ContractSchema<PrepackagedLabelEditorPresetId> =
  contractSchema(isPresetId);
export const PrepackagedLabelEditorBlockIdSchema: ContractSchema<PrepackagedLabelEditorBlockId> =
  contractSchema(isBlockId);

/**
 * V5 stores a manifest, not a user-editable preset. Validate it against the
 * frozen shared catalogue so backend and frontend cannot drift independently.
 */
export const isCanonicalPrepackagedLabelEditorPreset = (
  value: unknown,
): value is PrepackagedLabelEditorPreset => {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ['id', 'manifestVersion', 'blockOrder', 'columns', 'typography'])
  ) {
    return false;
  }
  if (
    !isPresetId(value.id) ||
    value.manifestVersion !== 1 ||
    ![1, 2].includes(Number(value.columns))
  ) {
    return false;
  }
  const blockOrder = value.blockOrder;
  if (!Array.isArray(blockOrder) || !blockOrder.every(isBlockId)) {
    return false;
  }
  const typography = value.typography;
  if (
    !isRecord(typography) ||
    !hasExactKeys(typography, ['fontFamily', 'baseFontSizeMm', 'minXHeightMm']) ||
    !isNonEmptyString(typography.fontFamily) ||
    !isPositiveMm(typography.baseFontSizeMm) ||
    !isPositiveMm(typography.minXHeightMm)
  ) {
    return false;
  }
  const canonical = PREPACKAGED_LABEL_EDITOR_PRESETS.find((preset) => preset.id === value.id);
  return (
    canonical !== undefined &&
    canonical.columns === value.columns &&
    canonical.blockOrder.length === blockOrder.length &&
    canonical.blockOrder.every((block, index) => block === blockOrder[index]) &&
    canonical.typography.fontFamily === typography.fontFamily &&
    canonical.typography.baseFontSizeMm === typography.baseFontSizeMm &&
    canonical.typography.minXHeightMm === typography.minXHeightMm
  );
};
export const PrepackagedLabelEditorPresetSchema = contractSchema(
  isCanonicalPrepackagedLabelEditorPreset,
);

export interface PrepackagedLabelLayoutBlockV2 {
  id: PrepackagedLabelEditorBlockId;
  token: PrepackagedLabelEditorBlockId;
  required: boolean;
  visible: boolean;
  geometry: { xMm: number; yMm: number; widthMm: number; heightMm: number };
  typography?: {
    fontSizeMm: number;
    fontWeight: number;
    lineHeight: number;
    align: 'LEFT' | 'CENTER' | 'RIGHT';
  };
  /**
   * True only for a block `addRequiredBlocksToDocument`
   * (`shared/src/label/required-block-placement.ts`) placed and the operator
   * has never since moved or resized (`updateLayoutBlockGeometry`,
   * `shared/src/label/layout-v2/layout-solver.ts`, flips it to `false` the
   * moment it does). `downgradeStaleRequiredBlocks`
   * (`shared/src/label/required-label-blocks.ts`) reads this to tell
   * "nobody but the machine ever put this here" apart from "there is human
   * work on this block" — only the first may be silently removed once the
   * block stops being due. Absent on every document written before this
   * field existed: treated the same as `false` (never auto-remove), which is
   * the conservative side, not an assumption that the block was untouched.
   */
  autoPlaced?: boolean;
  /** Assistant provenance, also allowed on blocks (`ADD_BLOCK`/`UPDATE_ELEMENT`). */
  provenance?: PrepackagedLabelLayoutProvenanceV2;
}

export const PREPACKAGED_LABEL_LAYOUT_ASSET_KINDS = [
  'LOGO',
  'GLYPH',
  'CROP',
  'TEXT',
  'LINE',
  'RECT',
  'ELLIPSE',
  'BARCODE',
  'QR',
] as const;
export type PrepackagedLabelLayoutAssetKind = (typeof PREPACKAGED_LABEL_LAYOUT_ASSET_KINDS)[number];
/** Kinds whose bytes live in storage behind a sha256 `contentHash`. */
export const STORED_ASSET_KINDS = ['LOGO', 'GLYPH', 'CROP'] as const;
export type StoredAssetKind = (typeof STORED_ASSET_KINDS)[number];
/** Pure-decoration kinds: drawn under the blocks and exempt from collisions. */
export const DECORATIVE_ASSET_KINDS = ['LINE', 'RECT', 'ELLIPSE'] as const;
export type DecorativeAssetKind = (typeof DECORATIVE_ASSET_KINDS)[number];

/**
 * The one gate every "does this kind travel as stored bytes" decision goes
 * through. Call sites used to hard-code `!== 'TEXT'`, which silently promoted
 * every future generated kind (BARCODE, QR, shapes) to a stored-byte lookup.
 */
export const isStoredAssetKind = (kind: PrepackagedLabelLayoutAssetKind): boolean =>
  (STORED_ASSET_KINDS as readonly string[]).includes(kind);

export const isDecorativeAssetKind = (kind: PrepackagedLabelLayoutAssetKind): boolean =>
  (DECORATIVE_ASSET_KINDS as readonly string[]).includes(kind);

/** Decorative shapes never collide: drawn under the blocks, never relocated. */
export const collidesInLayout = (asset: { kind: PrepackagedLabelLayoutAssetKind }): boolean =>
  !isDecorativeAssetKind(asset.kind);

/** Every client-generated kind lives in its own id namespace, never in storage. */
export const GENERATED_ASSET_ID_PATTERN = /^(text|line|rect|ellipse|barcode|qr)-[a-z0-9-]{1,64}$/;
export const MAX_LAYOUT_ASSETS_PER_LABEL = 40;
/** Was 5: the free-mode editor turns free text into a first-class palette element. */
export const MAX_TEXT_ASSETS_PER_LABEL = 20;
export const TEXT_ASSET_ID_PATTERN = /^text-[a-z0-9-]{1,64}$/;
export const MAX_TEXT_ASSET_LENGTH = 200;
/** Only assets rotate; blocks stay axis-aligned. Whole degrees, bounds included. */
export const ASSET_ROTATION_MIN_DEG = -180;
export const ASSET_ROTATION_MAX_DEG = 180;
/** Printable font families shared by the SVG canvas and the PDF renderer
 *  (pdf-lib standard fonts, WinAnsi-safe). */
export const PREPACKAGED_LABEL_TEXT_FONT_KEYS = ['SANS', 'SERIF', 'MONO'] as const;
export type PrepackagedLabelTextFontKey = (typeof PREPACKAGED_LABEL_TEXT_FONT_KEYS)[number];
/** Lowercase `#rrggbb` only: the print pipeline stores exactly what it draws. */
export const TEXT_ASSET_COLOR_PATTERN = /^#[0-9a-f]{6}$/;
export const PREPACKAGED_LABEL_TEXT_ALIGNS = ['LEFT', 'CENTER'] as const;
export type PrepackagedLabelTextAlign = (typeof PREPACKAGED_LABEL_TEXT_ALIGNS)[number];
export const PREPACKAGED_LABEL_LAYOUT_ASSET_PLACEMENTS = [
  'FREEFORM',
  'HEADER_LEFT',
  'HEADER_CENTER',
  'HEADER_RIGHT',
] as const;
export type PrepackagedLabelLayoutAssetPlacement =
  (typeof PREPACKAGED_LABEL_LAYOUT_ASSET_PLACEMENTS)[number];

/** The line runs from (x,y) to (x+w,y+h) when DOWN, from (x,y+h) to (x+w,y) when UP. */
export const PREPACKAGED_LABEL_LINE_DIRECTIONS = ['DOWN', 'UP'] as const;
export type PrepackagedLabelLineDirection = (typeof PREPACKAGED_LABEL_LINE_DIRECTIONS)[number];
export const PREPACKAGED_LABEL_BARCODE_SYMBOLOGIES = ['EAN13', 'EAN8'] as const;
export type PrepackagedLabelBarcodeSymbology =
  (typeof PREPACKAGED_LABEL_BARCODE_SYMBOLOGIES)[number];
export const PREPACKAGED_LABEL_BARCODE_SOURCES = ['PROJECT_GTIN', 'LITERAL'] as const;
export type PrepackagedLabelBarcodeSource = (typeof PREPACKAGED_LABEL_BARCODE_SOURCES)[number];
export const PREPACKAGED_LABEL_QR_ERROR_CORRECTIONS = ['M', 'Q'] as const;
export type PrepackagedLabelQrErrorCorrection =
  (typeof PREPACKAGED_LABEL_QR_ERROR_CORRECTIONS)[number];
export const MAX_QR_PAYLOAD_LENGTH = 512;

/** Outline and fill of a decorative shape; lowercase `#rrggbb`, `null` = no fill. */
export interface PrepackagedLabelAssetShapeV2 {
  strokeWidthMm: number;
  strokeColor: string;
  fillColor: string | null;
}

export interface PrepackagedLabelAssetBarcodeV2 {
  symbology: PrepackagedLabelBarcodeSymbology;
  /** PROJECT_GTIN resolves the value from the project's `gtin` at compose time. */
  source: PrepackagedLabelBarcodeSource;
  /** Literal GTIN when `source === 'LITERAL'` (check digit verified), else null. */
  value: string | null;
  humanReadable: boolean;
}

export interface PrepackagedLabelAssetQrV2 {
  /** 1–512 characters after trim. */
  payload: string;
  errorCorrection: PrepackagedLabelQrErrorCorrection;
}

/**
 * Marcatura AI Act art. 50: present on elements the assistant last wrote,
 * cleared by the canvas at the first manual edit of the same element.
 */
export interface PrepackagedLabelLayoutProvenanceV2 {
  source: 'ASSISTANT';
  messageId: string;
  appliedAt: string;
}

export interface PrepackagedLabelLayoutAssetV2 {
  assetId: string;
  contentHash: string;
  kind: PrepackagedLabelLayoutAssetKind;
  required: boolean;
  visible: boolean;
  geometry: { xMm: number; yMm: number; widthMm: number; heightMm: number };
  placement?: PrepackagedLabelLayoutAssetPlacement;
  /** Free-text decoration, 1–200 characters after trim; only allowed on kind TEXT. */
  text?: string;
  /** Styling for the free-text decoration; only allowed on kind TEXT, absent = defaults. */
  fontKey?: PrepackagedLabelTextFontKey;
  bold?: boolean;
  color?: string;
  align?: PrepackagedLabelTextAlign;
  /** Only assets rotate: whole degrees in [-180, 180] around the geometry center. Absent = 0. */
  rotationDeg?: number;
  /** Only LINE/RECT/ELLIPSE. */
  shape?: PrepackagedLabelAssetShapeV2;
  /** Only LINE. */
  lineDirection?: PrepackagedLabelLineDirection;
  /** Only BARCODE. */
  barcode?: PrepackagedLabelAssetBarcodeV2;
  /** Only QR. */
  qr?: PrepackagedLabelAssetQrV2;
  /** Assistant provenance; cleared at the first manual edit. */
  provenance?: PrepackagedLabelLayoutProvenanceV2;
}

export const PREPACKAGED_LABEL_FACE_ROLES = ['PRINCIPAL', 'SECONDARY'] as const;
export type PrepackagedLabelFaceRole = (typeof PREPACKAGED_LABEL_FACE_ROLES)[number];

export interface PrepackagedLabelLayoutFaceV2 {
  /** Stable id chosen by the editor, e.g. `face-1`. */
  id: string;
  role: PrepackagedLabelFaceRole;
  /** Region of the page this face corresponds to, in mm. */
  geometry: { xMm: number; yMm: number; widthMm: number; heightMm: number };
}

export interface PrepackagedLabelLayoutDocumentV2 {
  schemaVersion: typeof PREPACKAGED_LABEL_LAYOUT_DOCUMENT_VERSION;
  widthMm: number;
  heightMm: number;
  presetId: PrepackagedLabelEditorPresetId;
  typography: { fontFamily: string; baseFontSizeMm: number; minXHeightMm: number };
  blocks: PrepackagedLabelLayoutBlockV2[];
  /** Optional for old V2 documents; expanded presets always emit an empty collection. */
  assets?: PrepackagedLabelLayoutAssetV2[];
  /**
   * The package's physical faces, as regions of the printed sheet. The sheet
   * stays one: the label prints flat and folds around the package, so folds
   * are lines on the page, not separate canvases. Absent = document predates
   * the face model: interpreted as a single PRINCIPAL face covering the
   * whole page.
   */
  faces?: PrepackagedLabelLayoutFaceV2[];
}

const hasTextStyleFields = (asset: Record<string, unknown>): boolean =>
  asset.fontKey !== undefined ||
  asset.bold !== undefined ||
  asset.color !== undefined ||
  asset.align !== undefined;

const hasValidTextStyle = (asset: Record<string, unknown>): boolean =>
  (asset.fontKey === undefined ||
    PREPACKAGED_LABEL_TEXT_FONT_KEYS.includes(asset.fontKey as PrepackagedLabelTextFontKey)) &&
  (asset.bold === undefined || typeof asset.bold === 'boolean') &&
  (asset.color === undefined ||
    (typeof asset.color === 'string' && TEXT_ASSET_COLOR_PATTERN.test(asset.color))) &&
  (asset.align === undefined ||
    PREPACKAGED_LABEL_TEXT_ALIGNS.includes(asset.align as PrepackagedLabelTextAlign));

/** TEXT assets are client-generated free text: no stored bytes (empty hash),
 *  ids in the text- namespace, and a usable 1-200 character payload. */
const hasValidTextAssetIdentity = (asset: Record<string, unknown>): boolean =>
  asset.contentHash === '' &&
  typeof asset.assetId === 'string' &&
  TEXT_ASSET_ID_PATTERN.test(asset.assetId) &&
  typeof asset.text === 'string' &&
  asset.text.trim().length >= 1 &&
  asset.text.length <= MAX_TEXT_ASSET_LENGTH &&
  (!hasTextStyleFields(asset) || hasValidTextStyle(asset));

const hasValidImageAssetIdentity = (asset: Record<string, unknown>): boolean =>
  isNonEmptyString(asset.assetId) &&
  isContentHash(asset.contentHash) &&
  asset.text === undefined &&
  asset.fontKey === undefined &&
  asset.bold === undefined &&
  asset.color === undefined &&
  asset.align === undefined;

/** Every generated kind carries an empty hash and an id in its own namespace. */
const hasValidGeneratedAssetIdentity = (asset: Record<string, unknown>): boolean =>
  asset.contentHash === '' &&
  typeof asset.assetId === 'string' &&
  GENERATED_ASSET_ID_PATTERN.test(asset.assetId);

/** `strokeWidthMm` in [0.1, 5], lowercase `#rrggbb` colors, `null` fill allowed. */
export const hasValidShapeIdentity = (shape: unknown): boolean =>
  isRecord(shape) &&
  hasExactKeys(shape, ['strokeWidthMm', 'strokeColor', 'fillColor']) &&
  typeof shape.strokeWidthMm === 'number' &&
  Number.isFinite(shape.strokeWidthMm) &&
  shape.strokeWidthMm >= 0.1 &&
  shape.strokeWidthMm <= 5 &&
  typeof shape.strokeColor === 'string' &&
  TEXT_ASSET_COLOR_PATTERN.test(shape.strokeColor) &&
  (shape.fillColor === null ||
    (typeof shape.fillColor === 'string' && TEXT_ASSET_COLOR_PATTERN.test(shape.fillColor)));

/** Literal GTIN values carry the symbology's exact digit count and a valid GS1 check digit. */
const barcodeValueIsValidLiteral = (value: string, symbology: string): boolean =>
  symbology === 'EAN8' ? /^\d{8}$/.test(value) : /^\d{13}$/.test(value);

/**
 * `value` is `null` when PROJECT_GTIN. On LITERAL it is the manual payload:
 * either still `null` — the draft the palette inserts, drawn as a reserved
 * «GTIN?» frame and never printed — or a check-digit valid literal carrying
 * the symbology's exact digit count.
 */
export const hasValidBarcodeIdentity = (barcode: unknown): boolean => {
  if (
    !isRecord(barcode) ||
    !hasExactKeys(barcode, ['symbology', 'source', 'value', 'humanReadable']) ||
    !PREPACKAGED_LABEL_BARCODE_SYMBOLOGIES.includes(
      barcode.symbology as PrepackagedLabelBarcodeSymbology,
    ) ||
    !PREPACKAGED_LABEL_BARCODE_SOURCES.includes(barcode.source as PrepackagedLabelBarcodeSource) ||
    typeof barcode.humanReadable !== 'boolean'
  ) {
    return false;
  }
  if (barcode.source === 'PROJECT_GTIN') {
    return barcode.value === null;
  }
  if (barcode.value === null) {
    return true;
  }
  return (
    typeof barcode.value === 'string' &&
    barcodeValueIsValidLiteral(barcode.value, String(barcode.symbology)) &&
    isValidGtin(barcode.value)
  );
};

/** `payload` 1–512 characters after trim, error correction M or Q. */
export const hasValidQrIdentity = (qr: unknown): boolean =>
  isRecord(qr) &&
  hasExactKeys(qr, ['payload', 'errorCorrection']) &&
  PREPACKAGED_LABEL_QR_ERROR_CORRECTIONS.includes(
    qr.errorCorrection as PrepackagedLabelQrErrorCorrection,
  ) &&
  typeof qr.payload === 'string' &&
  qr.payload.trim().length >= 1 &&
  qr.payload.trim().length <= MAX_QR_PAYLOAD_LENGTH;

const hasValidProvenance = (provenance: unknown): boolean =>
  isRecord(provenance) &&
  hasExactKeys(provenance, ['source', 'messageId', 'appliedAt']) &&
  provenance.source === 'ASSISTANT' &&
  isNonEmptyString(provenance.messageId) &&
  isNonEmptyString(provenance.appliedAt);

const hasValidRotationDeg = (rotationDeg: unknown): boolean =>
  typeof rotationDeg === 'number' &&
  Number.isInteger(rotationDeg) &&
  rotationDeg >= ASSET_ROTATION_MIN_DEG &&
  rotationDeg <= ASSET_ROTATION_MAX_DEG;

/** Optional cross-kind fields every asset may carry; presence must be well-formed. */
const hasValidCommonOptionalAssetFields = (asset: Record<string, unknown>): boolean =>
  (asset.rotationDeg === undefined || hasValidRotationDeg(asset.rotationDeg)) &&
  (asset.provenance === undefined || hasValidProvenance(asset.provenance));

/** LINE/RECT/ELLIPSE: shape optional (rendering defaults), payload keys absent. */
const hasValidShapeAssetIdentity = (asset: Record<string, unknown>): boolean =>
  hasValidGeneratedAssetIdentity(asset) &&
  (asset.shape === undefined || hasValidShapeIdentity(asset.shape)) &&
  (asset.lineDirection === undefined ||
    PREPACKAGED_LABEL_LINE_DIRECTIONS.includes(
      asset.lineDirection as PrepackagedLabelLineDirection,
    ));

/** The barcode payload IS the barcode asset's identity, like `text` for TEXT. */
const hasValidBarcodeAssetIdentity = (asset: Record<string, unknown>): boolean =>
  hasValidGeneratedAssetIdentity(asset) && hasValidBarcodeIdentity(asset.barcode);

const hasValidQrAssetIdentity = (asset: Record<string, unknown>): boolean =>
  hasValidGeneratedAssetIdentity(asset) && hasValidQrIdentity(asset.qr);

/**
 * The optional keys each kind may carry beyond the base set. The compiler is
 * the guardian: add a kind to `PREPACKAGED_LABEL_LAYOUT_ASSET_KINDS` without
 * an entry here and this file stops compiling.
 */
export const extraKeysByKind = {
  LOGO: ['placement', 'provenance'],
  GLYPH: ['placement', 'provenance'],
  CROP: ['placement', 'provenance'],
  TEXT: ['placement', 'text', 'fontKey', 'bold', 'color', 'align', 'rotationDeg', 'provenance'],
  LINE: ['placement', 'shape', 'lineDirection', 'rotationDeg', 'provenance'],
  RECT: ['placement', 'shape', 'rotationDeg', 'provenance'],
  ELLIPSE: ['placement', 'shape', 'rotationDeg', 'provenance'],
  BARCODE: ['placement', 'barcode', 'rotationDeg', 'provenance'],
  QR: ['placement', 'qr', 'rotationDeg', 'provenance'],
} as const satisfies Record<PrepackagedLabelLayoutAssetKind, readonly string[]>;

/**
 * Whether `kind` may carry `rotationDeg` at all — `extraKeysByKind` above is
 * the single source of truth. LOGO/GLYPH/CROP are stored raster bytes with no
 * rotated-rendering support in the print pipeline, so the schema never lists
 * `rotationDeg` for them and a payload that sets it is refused wholesale
 * (`hasExactKeys` fails). The canvas (rotation handle, ±90° context menu,
 * properties panel) reads this instead of hardcoding its own kind list, so an
 * offered control can never drift out of sync with what the schema accepts.
 */
export const assetKindSupportsRotation = (kind: PrepackagedLabelLayoutAssetKind): boolean =>
  (extraKeysByKind[kind] as readonly string[]).includes('rotationDeg');

/**
 * Per-kind identity rules (content hash, id namespace, required payload). A
 * kind without an entry here is a compile error, exactly like above.
 */
export const identityByKind = {
  LOGO: hasValidImageAssetIdentity,
  GLYPH: hasValidImageAssetIdentity,
  CROP: hasValidImageAssetIdentity,
  TEXT: hasValidTextAssetIdentity,
  LINE: hasValidShapeAssetIdentity,
  RECT: hasValidShapeAssetIdentity,
  ELLIPSE: hasValidShapeAssetIdentity,
  BARCODE: hasValidBarcodeAssetIdentity,
  QR: hasValidQrAssetIdentity,
} as const satisfies Record<
  PrepackagedLabelLayoutAssetKind,
  (asset: Record<string, unknown>) => boolean
>;

const ASSET_BASE_KEYS = ['assetId', 'contentHash', 'kind', 'required', 'visible', 'geometry'];

const assetExactKeysFor = (asset: Record<string, unknown>): string[] => {
  const extraKeys = extraKeysByKind[asset.kind as PrepackagedLabelLayoutAssetKind] ?? [];
  return [...ASSET_BASE_KEYS, ...extraKeys.filter((key) => asset[key] !== undefined)];
};

const isLayoutAssetV2 = (asset: unknown): boolean => {
  if (
    !isRecord(asset) ||
    !hasExactKeys(asset, assetExactKeysFor(asset)) ||
    !PREPACKAGED_LABEL_LAYOUT_ASSET_KINDS.includes(asset.kind as PrepackagedLabelLayoutAssetKind) ||
    typeof asset.required !== 'boolean' ||
    typeof asset.visible !== 'boolean'
  ) {
    return false;
  }
  const identityOf = identityByKind[asset.kind as PrepackagedLabelLayoutAssetKind];
  return (
    identityOf(asset) && hasValidCommonOptionalAssetFields(asset) && hasValidAssetGeometry(asset)
  );
};

/**
 * An element may be parked outside the label while the operator composes it —
 * only the part overlapping the page is printed — so a coordinate is allowed
 * to go negative down to the parking margin, and no further: a document
 * carrying a block a metre off the page is corrupt, not parked.
 */
const isCanvasCoordinateMm = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= -LAYOUT_OVERFLOW_MARGIN_MM;

const hasValidAssetGeometry = (asset: Record<string, unknown>): boolean => {
  const geometry = asset.geometry;
  return (
    isRecord(geometry) &&
    hasExactKeys(geometry, ['xMm', 'yMm', 'widthMm', 'heightMm']) &&
    isCanvasCoordinateMm(geometry.xMm) &&
    isCanvasCoordinateMm(geometry.yMm) &&
    isPositiveMm(geometry.widthMm) &&
    isPositiveMm(geometry.heightMm) &&
    (asset.placement === undefined ||
      PREPACKAGED_LABEL_LAYOUT_ASSET_PLACEMENTS.includes(
        asset.placement as PrepackagedLabelLayoutAssetPlacement,
      ))
  );
};

const countTextAssets = (assets: readonly unknown[]): number =>
  assets.filter((asset) => isRecord(asset) && asset.kind === 'TEXT').length;

const isLayoutFaceV2 = (face: unknown): face is PrepackagedLabelLayoutFaceV2 => {
  if (
    !isRecord(face) ||
    !hasExactKeys(face, ['id', 'role', 'geometry']) ||
    !isNonEmptyString(face.id) ||
    !(PREPACKAGED_LABEL_FACE_ROLES as readonly string[]).includes(face.role as string)
  ) {
    return false;
  }
  const geometry = face.geometry;
  return (
    isRecord(geometry) &&
    hasExactKeys(geometry, ['xMm', 'yMm', 'widthMm', 'heightMm']) &&
    isCanvasCoordinateMm(geometry.xMm) &&
    isCanvasCoordinateMm(geometry.yMm) &&
    isPositiveMm(geometry.widthMm) &&
    isPositiveMm(geometry.heightMm)
  );
};

/** At most one PRINCIPAL face, and no two faces sharing an id. */
const facesAreValid = (faces: unknown): boolean => {
  if (faces === undefined) {
    return true;
  }
  if (!Array.isArray(faces) || !faces.every(isLayoutFaceV2)) {
    return false;
  }
  const ids = faces.map((face) => face.id);
  const principalCount = faces.filter((face) => face.role === 'PRINCIPAL').length;
  return new Set(ids).size === ids.length && principalCount <= 1;
};

const blockExactKeysFor = (block: Record<string, unknown>): string[] => [
  'id',
  'token',
  'required',
  'visible',
  'geometry',
  ...(block.typography === undefined ? [] : ['typography']),
  ...(block.autoPlaced === undefined ? [] : ['autoPlaced']),
  ...(block.provenance === undefined ? [] : ['provenance']),
];

const isValidBlockGeometry = (geometry: unknown): boolean =>
  isRecord(geometry) &&
  hasExactKeys(geometry, ['xMm', 'yMm', 'widthMm', 'heightMm']) &&
  isCanvasCoordinateMm(geometry.xMm) &&
  isCanvasCoordinateMm(geometry.yMm) &&
  isPositiveMm(geometry.widthMm) &&
  isPositiveMm(geometry.heightMm);

const isValidBlockTypography = (typography: Record<string, unknown>): boolean =>
  isPositiveMm(typography.fontSizeMm) &&
  typeof typography.fontWeight === 'number' &&
  Number.isInteger(typography.fontWeight) &&
  typography.fontWeight >= 100 &&
  typography.fontWeight <= 900 &&
  typeof typography.lineHeight === 'number' &&
  Number.isFinite(typography.lineHeight) &&
  typography.lineHeight > 0 &&
  ['LEFT', 'CENTER', 'RIGHT'].includes(String(typography.align));

const isLayoutBlockV2 = (block: unknown): boolean => {
  if (
    !isRecord(block) ||
    !hasExactKeys(block, blockExactKeysFor(block)) ||
    !isBlockId(block.id) ||
    !isBlockId(block.token) ||
    typeof block.required !== 'boolean' ||
    typeof block.visible !== 'boolean' ||
    (block.autoPlaced !== undefined && typeof block.autoPlaced !== 'boolean') ||
    (block.provenance !== undefined && !hasValidProvenance(block.provenance)) ||
    !isValidBlockGeometry(block.geometry)
  ) {
    return false;
  }
  if (block.typography === undefined) {
    return true;
  }
  const typography = block.typography;
  return (
    isRecord(typography) &&
    hasExactKeys(typography, ['fontSizeMm', 'fontWeight', 'lineHeight', 'align']) &&
    isValidBlockTypography(typography)
  );
};

const isLayoutDocumentV2 = (input: unknown): input is PrepackagedLabelLayoutDocumentV2 => {
  if (
    !isRecord(input) ||
    !hasExactKeys(input, [
      'schemaVersion',
      'widthMm',
      'heightMm',
      'presetId',
      'typography',
      'blocks',
      ...(input.assets === undefined ? [] : ['assets']),
      ...(input.faces === undefined ? [] : ['faces']),
    ]) ||
    input.schemaVersion !== PREPACKAGED_LABEL_LAYOUT_DOCUMENT_VERSION ||
    !facesAreValid(input.faces)
  ) {
    return false;
  }
  if (
    !isPositiveMm(input.widthMm) ||
    !isPositiveMm(input.heightMm) ||
    !isPresetId(input.presetId)
  ) {
    return false;
  }
  const typography = input.typography;
  if (
    !isRecord(typography) ||
    !hasExactKeys(typography, ['fontFamily', 'baseFontSizeMm', 'minXHeightMm']) ||
    !isNonEmptyString(typography.fontFamily) ||
    !isPositiveMm(typography.baseFontSizeMm) ||
    !isPositiveMm(typography.minXHeightMm)
  ) {
    return false;
  }
  const assets = input.assets;
  const assetsValid =
    assets === undefined ||
    (Array.isArray(assets) &&
      assets.length <= MAX_LAYOUT_ASSETS_PER_LABEL &&
      assets.every(isLayoutAssetV2) &&
      countTextAssets(assets) <= MAX_TEXT_ASSETS_PER_LABEL);
  return assetsValid && Array.isArray(input.blocks) && input.blocks.every(isLayoutBlockV2);
};

export const PrepackagedLabelLayoutDocumentV2Schema = contractSchema(isLayoutDocumentV2);

export type PrepackagedLabelEditorAction =
  | { action: 'APPLY_PRESET'; payload: { presetId: PrepackagedLabelEditorPresetId } }
  | { action: 'SET_LABEL_FIELD'; payload: { field: string; value: unknown } }
  | { action: 'SET_LEGAL_DECISION'; payload: { decision: string; value: boolean } }
  | { action: 'ADD_REQUIRED_BLOCK'; payload: { blockId: PrepackagedLabelEditorBlockId } }
  | { action: 'SET_BLOCK_ORDER'; payload: { blockIds: PrepackagedLabelEditorBlockId[] } }
  | { action: 'USE_DERIVED_INGREDIENT_LIST'; payload: Record<string, never> }
  | { action: 'USE_DERIVED_ALLERGEN_EMPHASIS'; payload: Record<string, never> }
  | { action: 'ACCEPT_EXTRACTED_ASSET'; payload: { assetId: string; contentHash: string } }
  | { action: 'NAVIGATE_TO_FIELD'; payload: { field: string } }
  | { action: 'OPEN_RECIPE_SOURCE'; payload: { recipeId: string } };

const isEditorAction = (input: unknown): input is PrepackagedLabelEditorAction => {
  if (!isRecord(input) || typeof input.action !== 'string' || !isRecord(input.payload)) {
    return false;
  }
  const payload = input.payload;
  switch (input.action) {
    case 'APPLY_PRESET':
      return isPresetId(payload.presetId);
    case 'SET_LABEL_FIELD':
      return isNonEmptyString(payload.field) && 'value' in payload;
    case 'SET_LEGAL_DECISION':
      return isNonEmptyString(payload.decision) && typeof payload.value === 'boolean';
    case 'ADD_REQUIRED_BLOCK':
      return isBlockId(payload.blockId);
    case 'SET_BLOCK_ORDER':
      return (
        Array.isArray(payload.blockIds) &&
        payload.blockIds.length > 0 &&
        payload.blockIds.every(isBlockId) &&
        new Set(payload.blockIds).size === payload.blockIds.length
      );
    case 'USE_DERIVED_INGREDIENT_LIST':
    case 'USE_DERIVED_ALLERGEN_EMPHASIS':
      return Object.keys(payload).length === 0;
    case 'ACCEPT_EXTRACTED_ASSET':
      return isNonEmptyString(payload.assetId) && isContentHash(payload.contentHash);
    case 'NAVIGATE_TO_FIELD':
      return isNonEmptyString(payload.field);
    case 'OPEN_RECIPE_SOURCE':
      return isNonEmptyString(payload.recipeId);
    default:
      return false;
  }
};

export const PrepackagedLabelEditorActionSchema = contractSchema(isEditorAction);

export interface PrepackagedLabelCta {
  id: string;
  label: string;
  action: PrepackagedLabelEditorAction;
  evidenceIds: string[];
}
const isEvidenceIds = (value: unknown): value is string[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(isNonEmptyString) &&
  new Set(value).size === value.length;
const isCta = (input: unknown): input is PrepackagedLabelCta =>
  isRecord(input) &&
  isNonEmptyString(input.id) &&
  isNonEmptyString(input.label) &&
  isEditorAction(input.action) &&
  isEvidenceIds(input.evidenceIds);
export const PrepackagedLabelCtaSchema = contractSchema(isCta);

export interface PrepackagedLabelFinding {
  code: string;
  severity: 'INFO' | 'WARNING' | 'BLOCKING';
  message: string;
  field?: string;
  cta?: PrepackagedLabelCta;
  evidenceIds?: string[];
  /**
   * Present when `message` is itself an i18n key (`prepackagedLabel.…`)
   * rather than server-written prose: the interpolation values the
   * translation's `{{placeholders}}` need (e.g. `detail`, `printErrorCode`
   * for `central.compliance.printError.*`). Without this, a translation with
   * a placeholder renders the literal `{{…}}` instead of the value.
   */
  messageParams?: Record<string, unknown>;
}
const isFinding = (input: unknown): input is PrepackagedLabelFinding => {
  if (
    !isRecord(input) ||
    !isNonEmptyString(input.code) ||
    !['INFO', 'WARNING', 'BLOCKING'].includes(String(input.severity)) ||
    !isNonEmptyString(input.message) ||
    (input.field !== undefined && !isNonEmptyString(input.field))
  ) {
    return false;
  }
  const evidenceIds = input.evidenceIds;
  if (evidenceIds !== undefined && !isEvidenceIds(evidenceIds)) {
    return false;
  }
  if (input.cta === undefined) {
    return true;
  }
  return (
    isCta(input.cta) &&
    isEvidenceIds(evidenceIds) &&
    input.cta.evidenceIds.every((evidenceId) => evidenceIds.includes(evidenceId))
  );
};
export const PrepackagedLabelFindingSchema = contractSchema(isFinding);

export interface PrepackagedLabelImportAsset {
  assetId: string;
  contentHash: string;
  kind: 'SOURCE' | 'LOGO' | 'GLYPH' | 'CROP';
}
const isImportAsset = (input: unknown): input is PrepackagedLabelImportAsset =>
  isRecord(input) &&
  hasExactKeys(input, ['assetId', 'contentHash', 'kind']) &&
  isNonEmptyString(input.assetId) &&
  isContentHash(input.contentHash) &&
  ['SOURCE', 'LOGO', 'GLYPH', 'CROP'].includes(String(input.kind));
export const PrepackagedLabelImportAssetSchema = contractSchema(isImportAsset);

export interface PrepackagedLabelImportResult {
  schemaVersion: 1;
  sessionId: string;
  status: 'QUEUED' | 'ANALYZING' | 'READY' | 'FAILED' | 'APPLIED' | 'EXPIRED';
  recommendedPresetId?: PrepackagedLabelEditorPresetId;
  findings: PrepackagedLabelFinding[];
  assets: PrepackagedLabelImportAsset[];
}
const isImportResult = (input: unknown): input is PrepackagedLabelImportResult =>
  isRecord(input) &&
  input.schemaVersion === 1 &&
  isNonEmptyString(input.sessionId) &&
  ['QUEUED', 'ANALYZING', 'READY', 'FAILED', 'APPLIED', 'EXPIRED'].includes(String(input.status)) &&
  (input.recommendedPresetId === undefined || isPresetId(input.recommendedPresetId)) &&
  Array.isArray(input.findings) &&
  input.findings.every(isFinding) &&
  Array.isArray(input.assets) &&
  input.assets.every(isImportAsset);
export const PrepackagedLabelImportResultSchema = contractSchema(isImportResult);

export function findPrepackagedLabelEditorPreset(
  id: PrepackagedLabelEditorPresetId,
): PrepackagedLabelEditorPreset {
  const found = PREPACKAGED_LABEL_EDITOR_PRESETS.find((preset) => preset.id === id);
  if (!found) {
    throw new Error(`unknown prepackaged label editor preset: ${id}`);
  }
  return found;
}
